import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  intermediaryGetIntermediaryFcaData,
  FcaResponseResult,
  IntermediaryFcaData,
  intermediarySendInvitationEmail,
  CognitoUserStatus,
} from '@pmp/api';
import { notification } from '@ohpen/enso-ui';
import { getApiErrorMessage } from '@pmp/utils';

import { TabProps } from '../interfaces';
import { showFcaNotification } from '../../firms/utils';
import { PersonalDetailsTab } from './PersonalDetailsTab';
import { ContactDetailsTab } from './ContactDetailsTab';
import { StatusTab } from './StatusTab';
import { TabContent } from './TabContent';

export const BrokerDetailsTab: React.FC<TabProps> = ({ form, mode, selectsOptions, defaultComplianceStatus }) => {
  const { t } = useTranslation();
  const [fcaModifiedFields, setFcaModifiedFields] = useState<IntermediaryFcaData | null | undefined>(null);

  const populateData = useCallback(
    (irn: string): Promise<void> => {
      return intermediaryGetIntermediaryFcaData({ path: { irn } })
        .then(({ data }) => {
          const { result, registrationDetails, personalDetails } = data;
          if (!result) return;

          if (result === FcaResponseResult.OK || result === FcaResponseResult.PARTIAL_DATA) {
            form?.setFieldsValue({
              irn: registrationDetails?.irn,
              fcaStatus: registrationDetails?.fcaStatus,
              personalData: {
                ...form?.getFieldValue('personalData'),
                firstName: personalDetails?.firstName,
                lastName: personalDetails?.lastName,
              },
            });
            setFcaModifiedFields(data);
          }

          showFcaNotification(result, t, notification());
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
        });
    },
    [form, t],
  );

  const removeFcaModifiedField = useCallback((key: keyof IntermediaryFcaData, nestedKey?: string) => {
    setFcaModifiedFields(prev => {
      const next = { ...prev };
      if (nestedKey && next[key]) {
        const nested = { ...(next[key] as Record<string, unknown>) };
        delete nested[nestedKey];
        next[key] = nested as never;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const inviteIntermediary = useCallback((): Promise<void> => {
    const id = form?.getFieldValue('id');
    if (!id) {
      notification().error({ title: t('global.errors.unexpected') });
      return Promise.resolve();
    }
    return intermediarySendInvitationEmail({ path: { id } })
      .then(() => {
        form?.setFieldsValue({ cognitoUserStatus: CognitoUserStatus.FORCE_CHANGE_PASSWORD });
        notification().success({ title: t('lender.brokers.notifications.invitationSent') });
      })
      .catch(error => {
        notification().error({ title: getApiErrorMessage(error) });
      });
  }, [form, t]);

  return (
    <TabContent>
      <PersonalDetailsTab
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        fcaModifiedFields={fcaModifiedFields}
        populateFcaData={populateData}
        removeFcaModifiedField={removeFcaModifiedField}
        inviteIntermediary={inviteIntermediary}
      />
      <ContactDetailsTab form={form} mode={mode} selectsOptions={selectsOptions} />
      <StatusTab form={form} mode={mode} selectsOptions={selectsOptions} defaultComplianceStatus={defaultComplianceStatus} />
    </TabContent>
  );
};
